export const OUTLET_TYPE_OPTIONS = [
  { value: 'gym_center', label: 'Gym Center' },
  { value: 'gym_store', label: 'Gym + Store' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'pos_store', label: 'POS Store' },
  { value: 'building_material_store', label: 'Store Bahan Bangunan' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'cafe_gym', label: 'Cafe + Gym' },
  { value: 'cafe_store', label: 'Cafe + Store' },
];

function extractRawOutletType(value) {
  if (typeof value === 'object' && value !== null) {
    return value.String ?? value.string ?? value.value ?? '';
  }

  return value || '';
}

export function normalizeOutletTypeValue(value) {
  const normalized = String(extractRawOutletType(value))
    .trim()
    .toLowerCase()
    .replace(/[+\s/-]+/g, '_');

  switch (normalized) {
    case 'gym':
    case 'fitness':
    case 'gym_center':
      return 'gym_center';
    case 'gym_store':
    case 'gym_and_store':
    case 'store_gym':
    case 'store_and_gym':
      return 'gym_store';
    case 'restaurant':
    case 'resto':
    case 'cafe':
    case 'coffee_shop':
      return 'restaurant';
    case 'laundry':
    case 'laundry_service':
      return 'laundry';
    case 'pos':
    case 'store':
    case 'retail':
    case 'shop':
    case 'pos_store':
      return 'pos_store';
    case 'building_material_store':
    case 'store_bahan_bangunan':
    case 'toko_bahan_bangunan':
    case 'bahan_bangunan_store':
    case 'hardware_store':
    case 'material_store':
    case 'toko_bangunan':
    case 'toko_material':
      return 'building_material_store';
    case 'gym_pos':
    case 'gym_and_pos':
    case 'hybrid':
      return 'hybrid';
    case 'cafe_gym':
    case 'cafe_and_gym':
    case 'gym_cafe':
    case 'gym_and_cafe':
      return 'cafe_gym';
    case 'cafe_store':
    case 'cafe_and_store':
    case 'store_cafe':
    case 'store_and_cafe':
      return 'cafe_store';
    default:
      return '';
  }
}

export function resolveOutletTypeLabel(value) {
  const normalizedValue = normalizeOutletTypeValue(value);
  return OUTLET_TYPE_OPTIONS.find(option => option.value === normalizedValue)?.label || 'Not set';
}

const FITNESS_OUTLET_TYPES = ['gym_center', 'gym_store', 'hybrid', 'cafe_gym'];
const POS_OUTLET_TYPES = ['gym_store', 'restaurant', 'laundry', 'pos_store', 'building_material_store', 'hybrid', 'cafe_gym', 'cafe_store'];
const POS_ONLY_OUTLET_TYPES = ['restaurant', 'laundry', 'pos_store', 'building_material_store', 'cafe_store'];

const OUTLET_EXPERIENCE_CONFIGS = {
  gym_center: {
    displayLabel: 'Gym',
    dashboardMode: 'fitness',
    reportTabs: ['business', 'accounting'],
    analyticsDescription:
      'Workspace analytics gym difokuskan pada membership, check-in, private session, dan accounting outlet.',
  },
  gym_store: {
    displayLabel: 'Gym + Store',
    dashboardMode: 'hybrid',
    reportTabs: ['business', 'pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics gym + store menggabungkan performa member dengan transaksi POS dan accounting outlet.',
  },
  restaurant: {
    displayLabel: 'Cafe / Restaurant',
    dashboardMode: 'pos',
    reportTabs: ['pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics cafe difokuskan pada omzet POS, menu/produk, kasir, vendor, dan accounting outlet.',
  },
  laundry: {
    displayLabel: 'Laundry',
    dashboardMode: 'pos',
    reportTabs: ['pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics laundry difokuskan pada transaksi jasa, ritme order aktif, revenue outlet, dan accounting.',
  },
  pos_store: {
    displayLabel: 'Store',
    dashboardMode: 'pos',
    reportTabs: ['pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics store difokuskan pada penjualan produk, stok, vendor, dan accounting outlet.',
  },
  building_material_store: {
    displayLabel: 'Store Bahan Bangunan',
    dashboardMode: 'pos',
    reportTabs: ['pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics toko bahan bangunan difokuskan pada penjualan retail proyek, piutang customer, stok, vendor, dan accounting outlet.',
  },
  hybrid: {
    displayLabel: 'Gym + POS',
    dashboardMode: 'hybrid',
    reportTabs: ['business', 'pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics hybrid menggabungkan operasional member, transaksi POS, dan accounting dalam satu tampilan.',
  },
  cafe_gym: {
    displayLabel: 'Cafe + Gym',
    dashboardMode: 'hybrid',
    reportTabs: ['business', 'pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics cafe + gym menggabungkan alur member, attendance, layanan, dan penjualan POS outlet.',
  },
  cafe_store: {
    displayLabel: 'Cafe + Store',
    dashboardMode: 'pos',
    reportTabs: ['pos', 'accounting'],
    analyticsDescription:
      'Workspace analytics cafe + store difokuskan pada penjualan, stok, vendor, dan accounting outlet.',
  },
};

export function isFitnessOutletType(value) {
  return FITNESS_OUTLET_TYPES.includes(normalizeOutletTypeValue(value));
}

export function isPosOutletType(value) {
  return POS_OUTLET_TYPES.includes(normalizeOutletTypeValue(value));
}

export function isPosOnlyOutletType(value) {
  return POS_ONLY_OUTLET_TYPES.includes(normalizeOutletTypeValue(value));
}

export function resolveOutletExperience(value) {
  const type = normalizeOutletTypeValue(value);
  const config = OUTLET_EXPERIENCE_CONFIGS[type] || {};

  return {
    type,
    displayLabel: config.displayLabel || resolveOutletTypeLabel(type || value) || 'Outlet',
    dashboardMode: config.dashboardMode || (isPosOnlyOutletType(type) ? 'pos' : isPosOutletType(type) && isFitnessOutletType(type) ? 'hybrid' : 'fitness'),
    reportTabs: Array.isArray(config.reportTabs) && config.reportTabs.length > 0
      ? config.reportTabs
      : ['business', 'pos', 'accounting'],
    analyticsDescription:
      config.analyticsDescription ||
      'Workspace analytics outlet ini menyesuaikan domain business, POS, dan accounting sesuai tipe outlet yang aktif.',
    supportsFitness: isFitnessOutletType(type),
    supportsPos: isPosOutletType(type),
    isPosOnly: isPosOnlyOutletType(type),
  };
}
